/**
 * MUS-505 - resume a sight-reading session after a tab-away / inactivity
 * interruption.
 *
 * When a session is interrupted (window blur / tab hidden) the app pauses
 * playback and the browser SUSPENDS the Tone.js audio clock. Nothing used to
 * resume that clock or re-arm playback on return, so the session was stranded:
 * the clock stayed frozen and the cursor/score never advanced even after the
 * user came back to the tab.
 *
 * This helper is the pure orchestration behind the fix, with its side effects
 * injected, so the semantics can be unit tested without rendering the (very
 * large) MainAppPhaser component. The wiring in MainAppPhaser supplies the real
 * Tone.js / Redux dependencies and registers the returned handler on the focus
 * and visibilitychange events.
 */

export interface InterruptionResumeDeps {
  /** True if a session was actively playing when focus/visibility was lost. */
  wasPlaying: () => boolean;
  /**
   * Clear the "was playing" flag. Called synchronously before any await so the
   * handler is idempotent when both focus and visibilitychange fire on return.
   */
  clearWasPlaying: () => void;
  /** Current audio-clock state ('suspended' | 'running' | 'closed' | ...). */
  audioClockState: () => AudioContextState;
  /** Resume the (browser-suspended) audio clock. Only called when suspended. */
  resumeAudioClock: () => Promise<void>;
  /** Re-arm playback (resumes from the preserved measure). */
  replay: () => void;
  /** Optional error sink for a failed clock resume; never throws to the caller. */
  onError?: (e: unknown) => void;
}

/**
 * Build an idempotent async handler that, on focus/visibility return, resumes a
 * session that was involuntarily paused:
 *   1. No-op unless a session was playing when we lost focus.
 *   2. Resume the audio clock, but only if it is actually suspended.
 *   3. Re-arm playback (which resumes from the player's preserved place).
 *
 * The handler is safe to wire to BOTH the focus and visibilitychange events:
 * the was-playing flag is cleared synchronously before the first await, so a
 * near-simultaneous second event is a no-op rather than a double-resume.
 */
export function makeInterruptionResumer(deps: InterruptionResumeDeps): () => Promise<void> {
  return async () => {
    if (!deps.wasPlaying()) return;
    // Synchronous, pre-await: guards against a double-resume when focus and
    // visibilitychange both fire on tab return.
    deps.clearWasPlaying();

    try {
      if (deps.audioClockState() === 'suspended') {
        await deps.resumeAudioClock();
      }
    } catch (e) {
      deps.onError?.(e);
    }

    // Re-arm playback even if the clock was already running (e.g. the browser
    // did not suspend it): the session still needs its play state restored.
    deps.replay();
  };
}
